import { createListenerMiddleware } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { resolveUserTier, UserTier } from '@/entities/permissions';
import { setUserPermissions } from '@/shared/store/userSlice';
import { logout } from '@/slices/authSlice';
import type { RootState } from '@/store';
import type { UserSubscription } from '@/types/auth';
import getTimeDifference from '@/utils/getTimeDifference';
import isDateExpired from '@/utils/isDateExpired';

const computePermissions = (subscription: UserSubscription | null) => {
  if (!subscription) {
    return {
      isExpired: undefined,
      userTier: resolveUserTier(null, false),
      timeToExpire: undefined
    };
  }
  const { expireAt, status } = subscription;
  const expiredDate = dayjs(expireAt.includes('Z') ? expireAt : `${expireAt}Z`);
  const isExpired = status !== 'active' || isDateExpired(expiredDate);
  const timeToExpire = isExpired ? undefined : getTimeDifference(expiredDate);
  return { isExpired, userTier: resolveUserTier(subscription, isExpired), timeToExpire };
};

export const permissionListener = createListenerMiddleware();

permissionListener.startListening({
  type: 'loginTrip/fulfilled',
  effect: async (_action, api) => {
    api.cancelActiveListeners(); // prevent duplicate loops on re-login

    // Set initial values immediately (before first 60s tick)
    const state = api.getState() as RootState;
    api.dispatch(setUserPermissions(computePermissions(state.tripToken.subscription)));

    // Refresh every 60 seconds
    while (true) {
      await api.delay(60_000);
      if (api.signal.aborted) return;
      const current = api.getState() as RootState;
      if (!current.tripToken.subscription) return;
      api.dispatch(setUserPermissions(computePermissions(current.tripToken.subscription)));
    }
  }
});

permissionListener.startListening({
  actionCreator: logout,
  effect: (_action, api) => {
    api.dispatch(
      setUserPermissions({
        isExpired: undefined,
        userTier: UserTier.Public,
        timeToExpire: undefined
      })
    );
  }
});
